import Phaser from 'phaser';
import { ANIMATION_CONFIG } from '@/config/constants';

/**
 * Animation Keys - Centralized animation identifiers
 */
export const ANIMATION_KEYS = {
  PLAYER: {
    IDLE: 'player_idle_anim',
    WALK: 'player_walk_anim',
    SHOOT: 'player_shoot_anim',
    FLINCH: 'player_flinch_anim',
    VICTORY: 'player_victory_anim',
    VICTORY_CLIP: 'player_victory_clip_anim',
    ANGRY: 'player_angry_anim',
    IDLE_ALT: 'player_idle_alt_anim',
  },
  BALL: {
    IDLE: 'ball_idle_anim',
  },
} as const;

/**
 * Asset Frame Keys - Keys for loaded sprite frames
 */
export const FRAME_KEYS = {
  PLAYER: {
    IDLE: 'player_idle_frame',
    WALK: 'player_walk_frame',
    SHOOT: 'player_shoot_frame',
  },
} as const;

/**
 * AnimationManager
 * Centralized utility for creating and managing game animations
 */
export class AnimationManager {
  /**
   * Create all player animations
   * Call this in BootScene.create() after loading sprite frames
   */
  static createPlayerAnimations(scene: Phaser.Scene): void {
    // Only create if not already created
    if (scene.anims.exists(ANIMATION_KEYS.PLAYER.IDLE)) {
      console.log('⚠️ Player animations already exist, skipping creation');
      return;
    }

    console.log('🎬 Creating player animations...');

    // IDLE Animation - using individual loaded frames (60 frames @ 600×760)
    const idleFrames: Phaser.Types.Animations.AnimationFrame[] = [];
    for (let i = 1; i <= 60; i++) {
      idleFrames.push({ key: `${FRAME_KEYS.PLAYER.IDLE}_${i}` });
    }

    scene.anims.create({
      key: ANIMATION_KEYS.PLAYER.IDLE,
      frames: idleFrames,
      frameRate: ANIMATION_CONFIG.IDLE_FPS,
      repeat: -1, // Loop forever
    });

    console.log('✅ Idle animation created (60 frames @ ' + ANIMATION_CONFIG.IDLE_FPS + ' fps)');

    // WALK Animation - using individual loaded frames (7 frames)
    const walkFrames: Phaser.Types.Animations.AnimationFrame[] = [];
    for (let i = 1; i <= 7; i++) {
      walkFrames.push({ key: `${FRAME_KEYS.PLAYER.WALK}_${i}` });
    }

    scene.anims.create({
      key: ANIMATION_KEYS.PLAYER.WALK,
      frames: walkFrames,
      frameRate: ANIMATION_CONFIG.RUN_FPS,
      repeat: -1, // Loop forever
    });
    console.log('✅ Walk animation created (7 frames @ ' + ANIMATION_CONFIG.RUN_FPS + ' fps)');

    // SHOOT Animation - using individual loaded frames (6 frames)
    const shootFrames: Phaser.Types.Animations.AnimationFrame[] = [];
    for (let i = 1; i <= 6; i++) {
      shootFrames.push({ key: `${FRAME_KEYS.PLAYER.SHOOT}_${i}` });
    }

    scene.anims.create({
      key: ANIMATION_KEYS.PLAYER.SHOOT,
      frames: shootFrames,
      frameRate: ANIMATION_CONFIG.SHOOT_FPS,
      repeat: 0, // Play once, then return to idle
    });
    console.log('✅ Shoot animation created (6 frames @ ' + ANIMATION_CONFIG.SHOOT_FPS + ' fps)');

    // ─── REACTIVE POSES — Polish Pass ────────────────────────────────────
    // 2-frame yoyo anims for flinch/victory/angry/idle_alt
    const makeReactive = (key: string, framePrefix: string, fps: number, repeat: number = 0) => {
      const frames: Phaser.Types.Animations.AnimationFrame[] = [];
      for (let i = 1; i <= 2; i++) frames.push({ key: `${framePrefix}_${i}` });
      // Skip if any frame is missing (key is string because we just built it above)
      if (!frames.every(f => typeof f.key === 'string' && scene.textures.exists(f.key as string))) {
        console.log(`⚠️ Skipping ${key} — missing frames`);
        return;
      }
      scene.anims.create({
        key,
        frames,
        frameRate: fps,
        repeat,
        yoyo: repeat < 0, // yoyo for looping anims, one-shot otherwise
      });
    };

    makeReactive(ANIMATION_KEYS.PLAYER.FLINCH,   'poseidon_flinch',   8,  0);
    makeReactive(ANIMATION_KEYS.PLAYER.VICTORY,  'poseidon_victory',  6,  0);
    makeReactive(ANIMATION_KEYS.PLAYER.ANGRY,    'poseidon_angry',    5, -1);
    makeReactive(ANIMATION_KEYS.PLAYER.IDLE_ALT, 'poseidon_idle_alt', 4,  0);
    console.log('✅ Reactive poses animations created (flinch/victory/angry/idle_alt)');

    // ─── VICTORY CLIP — boss-outro hero clip (Poseidon raises the V sign) ──
    // 30 transparent frames extracted from a generated clip; plays once at
    // 12fps (~2.5s) and holds on the final V-sign pose. Used only on the
    // boss victory screen.
    if (!scene.anims.exists(ANIMATION_KEYS.PLAYER.VICTORY_CLIP)) {
      const clipFrames: Phaser.Types.Animations.AnimationFrame[] = [];
      for (let i = 1; i <= 30; i++) clipFrames.push({ key: `poseidon_victory_clip_${i}` });
      if (clipFrames.every(f => scene.textures.exists(f.key as string))) {
        scene.anims.create({
          key: ANIMATION_KEYS.PLAYER.VICTORY_CLIP,
          frames: clipFrames,
          frameRate: 12,
          repeat: 0, // play once, hold final pose
        });
        console.log('✅ Victory clip animation created (30 frames @ 12 fps)');
      } else {
        console.log('⚠️ Skipping victory clip animation — missing frames');
      }
    }
  }

  /**
   * Create ball animations
   * Call this in BootScene.create() after loading ball sprite frames
   */
  static createBallAnimations(_scene: Phaser.Scene): void {
    // Placeholder for ball animations when needed
    console.log('🎬 Ball animations ready for implementation');
  }

  /**
   * Destroy all animations
   * Useful for cleanup or restarting
   */
  static destroyAllAnimations(scene: Phaser.Scene): void {
    scene.anims.remove(ANIMATION_KEYS.PLAYER.IDLE);
    scene.anims.remove(ANIMATION_KEYS.PLAYER.WALK);
    scene.anims.remove(ANIMATION_KEYS.PLAYER.SHOOT);
    console.log('🗑️ All animations destroyed');
  }
}
